/**
 * <s-read-more> — Clamps long copy on small screens with a "Read more" toggle.
 *
 * Markup:
 *   [data-read-more-body]   — The clamped region (CSS sets its collapsed max-height on mobile).
 *   [data-read-more-toggle] — Button; its label swaps between data-label-more / data-label-less.
 *
 * Sets `data-expanded` on itself when open and `data-clamped` when the copy is long enough
 * to need the toggle. The body's height animates between collapsed and full height.
 */
class SReadMore extends HTMLElement {
  #body = null;
  #toggle = null;
  #resizeObserver = new ResizeObserver(() => this.#measure());
  #onClick = () => this.#setExpanded(!this.hasAttribute('data-expanded'));

  connectedCallback() {
    this.#body = this.querySelector('[data-read-more-body]');
    this.#toggle = this.querySelector('[data-read-more-toggle]');
    if (!this.#body || !this.#toggle) return;
    this.#toggle.addEventListener('click', this.#onClick);
    this.#resizeObserver.observe(this.#body);
    this.#measure();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onClick);
    this.#resizeObserver.disconnect();
  }

  #measure() {
    if (this.hasAttribute('data-expanded')) return;
    const clamped = this.#body.scrollHeight > this.#body.clientHeight + 4;
    this.toggleAttribute('data-clamped', clamped);
  }

  #setExpanded(expanded) {
    const body = this.#body;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.toggleAttribute('data-expanded', expanded);
    this.#toggle.setAttribute('aria-expanded', String(expanded));
    this.#toggle.textContent = expanded ? this.#toggle.dataset.labelLess : this.#toggle.dataset.labelMore;

    if (expanded) {
      body.style.maxHeight = `${body.scrollHeight}px`;
      if (reduce) {
        body.style.maxHeight = 'none';
      } else {
        body.addEventListener(
          'transitionend',
          () => {
            if (this.hasAttribute('data-expanded')) body.style.maxHeight = 'none';
          },
          { once: true },
        );
      }
      return;
    }

    // Collapse: pin the full height, flush, then drop back to the CSS clamp so it transitions.
    body.style.maxHeight = `${body.scrollHeight}px`;
    void body.offsetHeight;
    body.style.maxHeight = '';
    if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('s-read-more')) customElements.define('s-read-more', SReadMore);
